import DSDrawer from '../../design-system/DSDrawer';
import MenuIcon from '@mui/icons-material/Menu';
import { NavigationDrawerItem } from '../../types';
import useNavigationDrawer from './hooks/useNavigationDrawer';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DSIconButton from '../../design-system/DSIconButton';
import DSBox from '../../design-system/DSBox';
import DSDrawerTabButton from '../../design-system/DSDrawerTabButton';
import { APPBAR_HIGHT, DRAWER_WIDTH } from '../../consts/general';
import { useTheme } from '@mui/material';
import { ReactComponent as DashboardIcon } from '../../design-system/Icons/DashboardIcon.svg';
import useDeviceType from '../../hooks/useDeviceType';
import { ReactComponent as CalendarIcon } from '../../design-system/Icons/CalendarIcon.svg';
import { ReactComponent as CollectionIcon } from '../../design-system/Icons/CollectionIcon.svg';
import { ReactComponent as PaymentIcon } from '../../design-system/Icons/payment.svg';
import DSHelpOutline from '../../design-system/DSHelpOutline';
import { useState } from 'react';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import firebaseService from '../../firebase/firebaseService';
import { AlertType } from '../../design-system/DSAlert';
import useAppContext from '../../hooks/useAppContext';
import WarningDialog from '../WarningDialog';
import PaymentModal from '../PaymentModal';

type NavigationDrawerProps = {
  pages: NavigationDrawerItem[];
};

const NavigationDrawer = ({ pages }: NavigationDrawerProps) => {
  const { isSmallLaptop } = useDeviceType();
  const { showAlert } = useAppContext();
  const { toggleDrawer, isDrawerOpen, handleClick, currentRoute } =
    useNavigationDrawer();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  const { zIndex, palette, spacing } = useTheme();

  const getIconForRoute = (route: string) => {
    switch (route) {
      case '/dashboard':
        return <DashboardIcon />;
      case '/klydos':
        return <AccessTimeIcon />;
      case '/calendar-events':
        return <CalendarIcon />;
      case '/collections':
        return <CollectionIcon />;
      case '/payment':
        return <PaymentIcon />;
      case '/help':
        return <DSHelpOutline sx={{ height: spacing(3), width: spacing(3) }} />;
      default:
        return null;
    }
  };

  return (
    <>
      {isSmallLaptop && !isDrawerOpen && (
        <DSIconButton
          onClick={() => toggleDrawer(true)}
          sx={{
            position: 'fixed',
            top: spacing(2),
            left: spacing(2),
            zIndex: zIndex.drawer,
            color: palette.text.primary,
          }}
        >
          <MenuIcon />
        </DSIconButton>
      )}

      <DSDrawer
        variant={isSmallLaptop ? 'temporary' : 'permanent'}
        anchor="left"
        open={isSmallLaptop ? isDrawerOpen : true}
        onClose={() => toggleDrawer(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: spacing(DRAWER_WIDTH),
            height: `calc(100% - ${spacing(APPBAR_HIGHT)})`,
            marginTop: spacing(APPBAR_HIGHT),
            borderRadius: '0px 28px 0px 0px',
            background:
              'var(--Background-Manu, linear-gradient(0deg, #0C0C0C 0%, #121212 100%))',
            boxShadow:
              '0px 1px 3px 0px rgba(0, 0, 0, 0.12), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 2px 1px -1px rgba(0, 0, 0, 0.20)',
            borderRight: 'none',
            p: 4,
          },
        }}
      >
        <DSBox
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: spacing(2),
          }}
        >
          {pages
            .filter((page) => page.route !== '/help')
            .map((page) => {
              const isSelected = currentRoute === page.route;
              return (
                <DSDrawerTabButton
                  key={page.route}
                  title={page.title}
                  startIcon={getIconForRoute(page.route)}
                  isSelected={isSelected}
                  disabled={page.disabled}
                  showNotification={page.showNotification}
                  onClick={() =>
                    page.route === '/payment'
                      ? setPaymentModalOpen(true)
                      : handleClick(page.route, page.externalUrl)
                  }
                />
              );
            })}
        </DSBox>

        <DSBox sx={{ mt: 'auto', width: '100%' }}>
          {pages
            .filter((page) => page.route === '/help')
            .map((page) => {
              const isSelected = currentRoute === page.route;
              return (
                <DSDrawerTabButton
                  key={page.route}
                  title={page.title}
                  startIcon={getIconForRoute(page.route)}
                  isSelected={isSelected}
                  disabled={page.disabled}
                  onClick={() => handleClick(page.route, page.externalUrl)}
                />
              );
            })}
          <DSDrawerTabButton
            title="Log Out"
            startIcon={
              <LogoutOutlinedIcon
                sx={{ height: spacing(3), width: spacing(3) }}
              />
            }
            onClick={() => setLogoutDialogOpen(true)}
          />
        </DSBox>
      </DSDrawer>
      <PaymentModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
      />
      <WarningDialog
        open={logoutDialogOpen}
        warningMessage="Are you sure you want to log out?"
        handleClose={() => setLogoutDialogOpen(false)}
        sx={{
          '& .MuiDialog-paper': {
            overflowY: 'visible',
            alignItems: 'center',
            justifyContent: 'center',
          },
          '& .MuiDialogContent-root': { pb: 1 },
          '& .MuiDialogActions-root': { pt: 2 },
        }}
        handleConfirmationClick={async () => {
          setLogoutDialogOpen(false);
          try {
            await firebaseService.signOut();
          } catch (error) {
            console.error('Error signing out:', error);
            showAlert({
              message: 'Failed to log out. Please try again.',
              alertType: AlertType.ERROR,
            });
          }
        }}
      />
    </>
  );
};

export default NavigationDrawer;
